import { useEffect, useState } from "react";
import type { BulkTier } from "@glastotaal/core";

/**
 * Staffelprijzen per artikel, apart van de rest van de productdetails.
 *
 * De winkelwagen moet de staffel opnieuw kunnen uitrekenen zodra je daar het aantal wijzigt —
 * anders blijft de stukprijs staan van het aantal waarmee je toevoegde, terwijl de server bij het
 * afrekenen wél de juiste staffel pakt (de klant betaalt dan minder dan de wagen aangeeft).
 * Het volledige detailbestand is ~2 MB; deze index alleen de staffels (~20 kB), en pas geladen
 * wanneer er een artikelregel in de wagen zit.
 */
export type BulkIndex = Record<string, BulkTier[]>;

let cache: BulkIndex | null = null;
let laden: Promise<BulkIndex> | null = null;

function laadIndex(): Promise<BulkIndex> {
  if (cache) return Promise.resolve(cache);
  if (!laden) {
    laden = import("../../data/seed/article-bulk.json")
      .then((m) => {
        cache = (m.default ?? {}) as BulkIndex;
        return cache;
      })
      .catch(() => {
        // Zonder de index rekent de wagen door met de opgeslagen stukprijs — dat is de oude
        // situatie, niet erger.
        cache = {};
        return cache;
      });
  }
  return laden;
}

/** Staffelprijzen voor de winkelwagen/afrekenpagina. Leeg tot de index geladen is. */
export function useBulkIndex(actief: boolean): BulkIndex {
  const [index, setIndex] = useState<BulkIndex>(cache ?? {});
  useEffect(() => {
    if (!actief || cache) return;
    let levend = true;
    void laadIndex().then((m) => { if (levend) setIndex(m); });
    return () => { levend = false; };
  }, [actief]);
  return cache ?? index;
}
